import { useBillingScheduleId } from 'BillingScheduleHome/utils/useBillingScheduleId'
import { parse } from '@sequencehq/utils/dist/dates'
import { useBillingScheduleAggregate } from 'BillingScheduleHome/utils/useBillingScheduleAggregate'
import { ScheduleTimeline } from '../timeline/ScheduleTimeline'
import { ErrorBoundary } from '@sentry/react'
import { Box, Flex } from '@chakra-ui/react'
import { GreyGrey30, GreyGrey70, Lato13Bold } from '@sequencehq/design-tokens'
import { Skeleton } from '@sequencehq/core-components'
import { TIMELINE_AXIS_HEIGHT_PX } from '../timeline/timeline.constants'

import { API_DATE_FORMAT } from 'BillingScheduleHome/billingScheduleHome.constants'
import { SectionError } from '../common/SectionError'

export const useScheduleTimelineData = (props: {
  billingScheduleId: string
}) => {
  const billingScheduleAggregateQuery = useBillingScheduleAggregate({
    billingScheduleId: props.billingScheduleId,
    select: data => {
      const scheduleStartDate = parse(
        data.billingSchedule.startDate,
        API_DATE_FORMAT,
        new Date()
      )
      const scheduleEndDate = data.billingSchedule.endDate
        ? parse(data.billingSchedule.endDate, API_DATE_FORMAT, new Date())
        : undefined

      return {
        phases: data.billingSchedule.phases,
        prices: data.prices,
        recurrenceDayOfMonth: data.billingSchedule.recurrenceDayOfMonth,
        startDate: scheduleStartDate,
        endDate: scheduleEndDate
      }
    }
  })

  return {
    isLoading: billingScheduleAggregateQuery.isFetching,
    isError: billingScheduleAggregateQuery.isError,
    scheduleTimelineData: billingScheduleAggregateQuery.data
  }
}

const TimelineError = () => {
  return (
    <Flex
      height={`${TIMELINE_AXIS_HEIGHT_PX}px`}
      alignItems="center"
      justifyContent="center"
      gap="4px"
      {...Lato13Bold}
      color={GreyGrey70}
      borderTop={`1px solid ${GreyGrey30}`}
    >
      <SectionError
        size="small"
        text="An error occurred when loading the timeline"
      />
    </Flex>
  )
}

export const SecheduleTimelineSection = () => {
  const billingScheduleId = useBillingScheduleId()
  const { isLoading, isError, scheduleTimelineData } = useScheduleTimelineData({
    billingScheduleId
  })

  if (isLoading || !scheduleTimelineData) {
    return (
      <Box
        height={`${TIMELINE_AXIS_HEIGHT_PX}px`}
        width="100%"
        padding="8px"
        borderTop={`1px solid ${GreyGrey30}`}
      >
        <Skeleton height="100%" width="100%" />
      </Box>
    )
  }

  if (isError) {
    return <TimelineError />
  }

  return (
    <ErrorBoundary fallback={() => <TimelineError />}>
      <ScheduleTimeline scheduleData={scheduleTimelineData} />
    </ErrorBoundary>
  )
}
